import { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import type { Asset } from 'types/asset.types';
import { type Device } from 'types/device.types';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { getAssetMakeModel } from 'views/FleetOverview/components/AssetCardsV2/utils';
import { composeUniqueKey } from 'utils/helpers/string.manipulation';
import { SensorAssignmentHeader } from './Header';
import EditableSensorTable from './Content';
import TireSensorHandler from 'handlers/tire.sensor.handler';
import { ThemePalette } from 'mui.theme';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const TpmSSensorAssignment = (): JSX.Element => {
  const { t } = useTranslation();
  const tireSensorHandler = new TireSensorHandler();
  const defaultAsset: string | null = new URLSearchParams(location.search).get(
    'asset'
  );
  const { assets, isLoadingAllAssets } = useAppSelector(
    (state) => state.assetReducer
  );
  const {
    role,
    customer: { theme },
  } = useAppSelector((state) => state.authReducer);
  const { data, isLoading } = useAppSelector(
    (state) => state.tireSensorReducer
  );
  const enableDarkTheme = isDarkTheme(theme);

  const tpmsAssets = useMemo(
    () => assets.filter((item) => item.tpmsEnabled),
    [assets]
  );
  const makeModels = getAssetMakeModel(tpmsAssets);
  const [selectedMakeModel, setSelectedMakeModel] = useState(() => {
    if (makeModels.length > 0) {
      return makeModels[0];
    } else {
      return '';
    }
  });

  const { id: customerId } = useAppSelector(
    (state) => state.persistedReducer
  ).customer;
  const tirePatterns = useAppSelector((state) => state.tirePatternReducer);

  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );

  const [assetBumperIds, setAssetBumperIds] = useState<string[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset>({
    assetId: '',
    assetVin: '',
    assetType: 'haul_truck',
    make: '',
    bumperNumber: '',
    engineHoursOffset: -1,
    device: {} as const as Device,
    isActive: false,
    model: '',
    odometerOffset: -1,
    fuelSource: '',
    operators: [],
    externalId: '',
    assetNotes: [],
  });

  useEffect(() => {
    if (defaultAsset) {
      const defaultAssetDetails = tpmsAssets.find(
        (item) => item.bumperNumber === defaultAsset
      );

      if (defaultAssetDetails) {
        const assetMakeModel = composeUniqueKey(
          [defaultAssetDetails.make, defaultAssetDetails.model],
          '-'
        );

        const bumperIdsOfSelectedMakeModel = tpmsAssets
          ?.filter(
            (asset) =>
              composeUniqueKey([asset.make, asset.model], '-') ===
              assetMakeModel
          )
          ?.map((asset) => asset.bumperNumber)
          .sort((a, b) =>
            a.localeCompare(b, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          );

        setSelectedAsset(defaultAssetDetails);
        setSelectedMakeModel(assetMakeModel);
        setAssetBumperIds(bumperIdsOfSelectedMakeModel);
      }
    } else {
      if (tpmsAssets.length > 0) {
        let topAsset = tpmsAssets.find((a) => a.bumperNumber === 'HT-535');
        if (!topAsset) {
          topAsset = tpmsAssets[0];
        }
        const topAssetMakeModel = composeUniqueKey(
          [topAsset.make, topAsset.model],
          '-'
        );
        const bumperIdsOfSelectedMakeModel = tpmsAssets
          ?.filter(
            (asset) =>
              composeUniqueKey([asset.make, asset.model], '-') ===
              topAssetMakeModel
          )
          ?.map((asset) => asset?.bumperNumber)
          .sort((a, b) =>
            a.localeCompare(b, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          );
        setSelectedAsset(topAsset);
        setSelectedMakeModel(topAssetMakeModel);
        setAssetBumperIds(bumperIdsOfSelectedMakeModel);
      }
    }
  }, [defaultAsset, tpmsAssets]);

  function handleSelectAsset(bumperNumber: string) {
    const item = tpmsAssets.find(
      (asset) => asset.bumperNumber === bumperNumber
    );
    if (item) {
      setSelectedAsset(item);
    }
  }

  const handleSelectMakeModel = (makeModel: string) => {
    try {
      const filteredAssets = tpmsAssets
        .filter(
          (asset) =>
            composeUniqueKey([asset.make, asset.model], '-') === makeModel
        )
        .map((item) => {
          return item.bumperNumber;
        })
        .sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
      setAssetBumperIds(filteredAssets);
      handleSelectAsset(filteredAssets[0]);
      setSelectedMakeModel(makeModel);
    } catch (error) {
      console.log('Error while selecting make model!');
    }
  };

  useEffect(() => {
    if (selectedAsset?.assetId !== '') {
      void tireSensorHandler.getAssetTires(selectedAsset.assetId);
    }
  }, [selectedAsset?.assetId]);

  const selectedAssetTires = useMemo(() => {
    return data[selectedAsset?.assetId] || [];
  }, [data, selectedAsset?.assetId]);

  function updateSensor(payload: any[]) {
    void tireSensorHandler.updateAssetTireSensor(
      selectedAsset.assetId,
      payload
    );
  }

  const { updatedByName, updatedAt } = useMemo(() => {
    if (!selectedAssetTires || selectedAssetTires.length === 0) {
      return { updatedByName: null, updatedAt: null };
    }
    const tireWithUpdate = selectedAssetTires.find(
      (t: any) => t.updatedByName || t.updatedAt
    );

    return {
      updatedByName: tireWithUpdate ? tireWithUpdate.updatedByName : null,
      updatedAt: tireWithUpdate ? tireWithUpdate.updatedAt : null,
    };
  }, [selectedAssetTires]);

  const palette =
    clientThemePalette[CLIENT_THEME][enableDarkTheme ? 'dark' : 'light'];
  const borderColor = palette.borderGrey || palette.border;

  return (
    <>
      {isLoading || isLoadingAllAssets ? (
        <SingleFadeLoader isFullScreen={true} />
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              padding: '10px',
              paddingBottom: '20px',
              borderRadius: '4px',
              marginTop: '10px',
              border: `1px solid ${borderColor}`,
            }}
          >
            <SensorAssignmentHeader
              makeModels={makeModels}
              enableDarkTheme={enableDarkTheme}
              selectedMakeModel={selectedMakeModel}
              setSelectedMakeModel={handleSelectMakeModel}
              assetBumperIds={assetBumperIds}
              selectedAsset={selectedAsset}
              setSelectedAsset={handleSelectAsset}
              updatedByName={updatedByName}
              updatedAt={updatedAt}
            />
          </Box>
          <Box
            sx={{
              marginTop: '20px',
              border: `1px solid ${borderColor}`,
              borderRadius: '4px',
            }}
          >
            {selectedAssetTires ? (
              <EditableSensorTable
                key={selectedAsset.assetId}
                enableDarkTheme={enableDarkTheme}
                assetTires={selectedAssetTires}
                selectedAsset={selectedAsset}
                updateSensor={updateSensor}
                role={role}
                makeModelDetails={makeModelDetails}
                tirePatterns={tirePatterns}
                customerId={customerId}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: palette.textPrimary,
                  padding: '20px',
                }}
              >
                {' '}
                {t('asset_tires_not_available')}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};
